import { AxiosResponse } from 'axios';
import { TCartItemVariant } from '../views/components';
import { IBrand } from './brand.types';
import { IAddress, IKitchen, KitchenRole } from './kitchen.types';
import { FoodItem, IMeal, IVariant, Option } from './meals.types';
import { ICreateCustomer, ICustomer, IUser } from './user.types';
import { PaginationParams, ResolvedResponse } from './general.types';
import { EmploymentType, PartnerStatus } from './pilots.types';

interface IOrdersApi {
  getAllOrders: (params: {
    page?: number;
    count?: number;
    query?: string;
  }) => Promise<AxiosResponse>;
  getAbandonedOrders: (params: {
    page?: number;
    query?: string;
  }) => Promise<AxiosResponse>;
  getOrder: (id: number) => Promise<AxiosResponse>;
  createOrder: (payload: ICreateOrder) => Promise<AxiosResponse>;
  updateOrder: (payload: ICreateOrder, id: number) => Promise<AxiosResponse>;
  deliveryFee: (address: IAddress, brands: number[]) => Promise<AxiosResponse>;
  createDeliveryRequest: (id: number) => Promise<AxiosResponse>;
  getOrderBySlug: (slug: string) => Promise<AxiosResponse>;
  exportOrderCsv: (query: string) => Promise<AxiosResponse>;
  getOrdersStatus: (params?: {
    channelId?: string;
    skipTenantIsolation?: boolean;
  }) => Promise<AxiosResponse>;
  getOrderCancelReasons: () => Promise<AxiosResponse>;
  createCancelReason: (payload: CancelReason) => Promise<AxiosResponse>;
  updateCancelReason: (
    id: string,
    payload: CancelReason
  ) => Promise<AxiosResponse>;
  getOrderChannels: () => Promise<AxiosResponse>;
  payForMe: (orderId: number) => Promise<AxiosResponse>;
  packOrder: (orderCode: string) => Promise<AxiosResponse>;
  getFlaggedOrders: (
    params?: FlaggedOrdersParams
  ) => Promise<ResolvedResponse<FlaggedOrder[]>>;
}

interface IOrder extends ICreateOrder {
  id: number;
  createdAt: string;
  updatedAt: string;

  slug: string;

  totalDiscount?: number;
  percentDiscount: number;
  freeDelivery: boolean;
  user: IUser;
  kitchens: IKitchen[];

  meals: IMeal[];
  brands: IBrand[];
  pilot: Pilot;
  pilotId: number;

  totalPrice: number;
  deliveryCharge: number;
  serviceCharge: number;

  customer?: ICustomer;

  customerType?: string;

  companyName?: string | null;
  companyOrderId?: string | null;
  companyOrder?: {
    company?: {
      id: string;
      name: string;
    };
  } | null;

  timeAccepted: null | string;
  timeArrived: null | string;
  timeDelivered: null | string;
  timePaid: null | string;
  timePickedUp: null | string;
  timePlaced: string | null;
  timePrepared: null | string;
  timeReady: null | string;
  timePreparing: string | null;
  orderItems: CreatedOrderItem[];
  scheduledTime: string | null;

  estimatedDistanceTravelled?: number;
  OrderService: OrderService[];
  riderStatus?: RiderStatus;

  Feedback?: Feedback[];
  userSegment?: userSegment;
  assignedKitchenStaffs?: { role: KitchenRole[]; user: IUser }[];
  metadata?: {
    orderId?: number;
    tenantId?: string;
    kitchenId?: number;
    tenantName?: string;
    pickupStoreName?: string;
    pickupContactPerson?: string;
    pickupPhoneNumber?: string;
    pickupEmail?: string;
    customerPhoneNumber?: string;
    orderItems?: Array<{
      name: string;
      quantity: number;
    }>;
    [key: string]: any;
  };
}

export interface userSegment {
  category?: string;
  tier?: CustomerTierKeys;
  experience?: string;
}

export interface Feedback {
  code: string;
  comment: string;
  createdAt: string;
  flagId: number;
  flags: { tag: string; value: string };
  id: number;
  orderId: number;
  pilot: {
    user: {
      email: string;
      firstName: string;
      lastName: string;
      phoneNumber: string;
    };
  };
  pilotId: number;
  user: null;
  userId: null;
}
export interface OrderService {
  CancelReason: null | string;
  cancelReasonId: null | number;
  code: string;
  createdAt: string;
  id: number;
  image: string;
  kitchenId: number;
  orderId: number;
  serviceStaffId: number;
  status: number;
  updatedAt: string;
}
interface ICreateOrder {
  userId?: number;
  customerId?: number;
  cartItems?: ICartItem[];
  notes?: string;
  riderNotes?: string;
  methodOfPayment?: PaymentMethod;
  deliveryAddress?: IAddress;
  customer?: ICreateCustomer | ICustomer;
  kitchens?: number[] | IKitchen[];
  paidFor?: boolean;
  status?: OrderStatus;
  pilot?: number | Pilot;
  cancelReason?: string;
  processRefund?: boolean;
  channelId?: string;
  channel?: OrderChannel;
  orderItems?: OrderItem[] | CreatedOrderItem[];
  CampaignOrder?: CampaignOrder;
}

interface ICartItem {
  id: number;
  quantity: number;
  brand: number | IBrand;
  meal: number | IMeal;
  variants?: number[] | IVariant[];
  cartItemVariants?: TCartItemVariant[];
  price?: number;
}

export interface CartItem {
  id: number;
  quantity: number;
  brand: IBrand;
  meal: IMeal;
  variants: IVariant[];
  cartItemVariants: CartItemVariant[];
}

export type CartItemVariant = { variant: IVariant; quantity: number };

type OrderStatus =
  | 'PLACED'
  | 'PREPARING'
  | 'ACCEPTED'
  | 'READY_FOR_PICKUP'
  | 'PREPARING'
  | 'ENROUTE'
  | 'SCHEDULED'
  | 'DELIVERED'
  | 'CONFIRMED'
  | 'CANCELED';

export type RiderStatus =
  | 'PENDING'
  | 'ACCEPTED'
  | 'ENROUTE'
  | 'ARRIVED'
  | 'DELIVERED';

export type CustomerTierKeys =
  | 'BRONZE'
  | 'SILVER'
  | 'GOLD'
  | 'PLATINUM'
  | 'DIAMOND'
  | 'ELITE'
  | 'CROWN';

export const CustomerTierDescriptions: Record<CustomerTierKeys, string> = {
  BRONZE: 'Bottom 25%',
  SILVER: '25%-50%',
  GOLD: '50%-75%',
  PLATINUM: '75%-90%',
  DIAMOND: '90%-95%',
  ELITE: '95%-99%',
  CROWN: 'Top 1%',
};

type PaymentMethod = 'TRANSFER' | 'WALLET' | 'CARD';

export interface Pilot {
  averageDeliveryTime: number;
  id: number;
  status: 'ACTIVE' | 'INACTIVE' | 'FLAGGED';
  user: {
    firstName: string;
    id: number;
    lastName: string;
    phoneNumber: string;
  };
}

export interface OrderParams {
  status?: OrderStatus[];
  notstatus?: OrderStatus[];
  count?: number;
  skipTenantIsolation?: boolean | string;
}

export interface DeliveryFee {
  fee: number;
  distance: number;
  unit?: string;
  estimatedDeliveryTime: number;
  charges?: {
    tax: number;
    service: number;
    chargeLimit: number;
  };
}

export type OrdersStatuses = { [x in OrderStatus]: number };

export interface CancelReason {
  createdAt?: string;
  id?: string;
  reason: string;
  updatedAt?: string;
  autoRefund: boolean;
  refundType: 'NO_REFUND' | 'FULL_REFUND' | 'MEAL_ONLY';
  includeDelivery: boolean;
  includeService: boolean;
}

export interface OrderChannel {
  id: string;
  createdAt: string;
  updatedAt: string;
  channelName: string;
}

export type {
  IOrder,
  ICreateOrder,
  OrderStatus,
  PaymentMethod,
  IOrdersApi,
  ICartItem,
  ICustomer,
};

export interface OrderItem {
  token?: string;
  foodItem: string;
  brand?: IBrand;
  name?: string;
  price: number;
  note?: string;

  listName?: string;

  quantity: number;
  options: {
    name?: string;
    quantity: number;
    option: string;
    price: number;
    group?: string;
  }[];

  customisations: {
    name?: string;
    quantity: number;
    option: string;
    price: number;
    group?: string;
  }[];
}

export interface CreatedOrderItem {
  foodItem: FoodItem & { listName?: string };
  quantity: number;
  options: { option: Option; quantity: number; id: string }[];
  customisations: { option: Option; quantity: number; id: string }[];
  notes: '';
  id: string;
  price: number;
}

export interface CampaignOrder {
  id: number;
  code: string;
  campaignId: number;
  orderId: number;
  status: string;
  campaign_status: string;
  reason: any;
  createdAt: string;
  updatedAt: string;
  campaign: Campaign;
}

export interface Campaign {
  id: number;
  code: string;
  categoryId: number;
  status: string;
  type: string;
  count: number;
  unitAmount: number;
  message: string;
  completedAt: any;
  expiresAt: any;
  budgetId: any;
  createdAt: string;
  updatedAt: string;
  tobeDisplayed: boolean;
  brandId: any;
  category: CampaignCategory;
  title: string;
}

export interface CampaignCategory {
  id: number;
  code: string;
  name: string;
  alias: any;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface FlaggedOrdersParams extends PaginationParams {
  vehicleType?: string;
}

export interface FlaggedOrder {
  id: number;
  code: string;
  createdAt: string;
  updatedAt: string;
  comment: string;
  pilotId: number;
  userId: null | number;
  flags: {
    id: number;
    value: string;
    tag: string;
  };
  order: {
    slug: string;
    id: number;
    code: string;
    orderItems: OrderItem[];
    status: OrderStatus;
  };
  pilot: {
    id: number;
    createdAt: string;
    email: null | string;
    phoneNumber: null | string;
    status: PartnerStatus;
    averageDeliveryTime: number;
    employmentType: EmploymentType;
    userId: number;
    partnerId: null | number;
    fleetCode: null;
  };
}
